import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdError } from 'react-icons/md';

import VideoDetailPlayerSection from '../../components/VideoDetailPlayerSection';
import VideoDetailLikesModal from '../../components/VideoDetailLikesModal';
import CommentsSection from '../../components/CommentsSection/CommentsSection';
import VideoDetailInfo from '../../components/VideoDetailInfo/VideoDetailInfo';
import VideoDetailDescription from '../../components/VideoDetailInfo/VideoDetailDescription';
import VideoImageCarousel from '../../components/VideoImageCarousel';
import VideoMaterialViewer from '../../components/VideoMaterialViewer';
import RelatedVideosList from '../../components/RelatedVideosList';
import LoadingIndicator from '../../components/LoadingIndicator';
import { toRelativePath } from '../../utils/videoImagePath';
import { useVideoDetail } from './useVideoDetail';
import { KebabMenuItem } from '../../components/VideoDetailInfo/types';

import styles from './VideoDetail.module.css';

export const VideoDetail = () => {
  const navigate = useNavigate();
  const {
    video,
    error,
    isConverting,
    isDeleted,
    playerRef,
    lastWatchSeconds,
    isMuted,
    hasLiked,
    likesCount,
    hasFavorited,
    likers,
    likeDialogOpen,
    videoMetadataRef,
    tagArray,
    hospitalName,
    uploaderName,
    uploaderIconUrl,
    hasImages,
    handlePlayerReady,
    handleDuration,
    handleProgress,
    handleEnded,
    handleTagClick,
    handleLike,
    handleFavorite,
    handleShowLikers,
    closeLikersDialog,
    handleComment,
    handleCommentWithTime,
    handleSeekToTime,
    handleToggleMute,
    handleAutoPlayError,
    isOwner,
    handleDownload
  } = useVideoDetail();

  // 共有ボタン用の状態
  const [isCopied, setIsCopied] = useState(false);
  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch((err) => {
      console.error('クリップボードへのコピーに失敗しました:', err);
    });
  }, []);

  // ケバブメニューアイテム（オーナーのみ編集・ダウンロードを表示）
  const kebabMenuItems = useMemo((): KebabMenuItem[] => {
    if (!isOwner || !video) return [];
    const items: KebabMenuItem[] = [
      {
        label: '編集',
        onClick: () => { navigate(`/videos/${video.id}/edit`); }
      },
      {
        label: 'ダウンロード',
        onClick: handleDownload
      }
    ];
    return items;
  }, [isOwner, video, navigate, handleDownload]);

  if (error) return <p>{error}</p>;
  if (!video) return <LoadingIndicator className={styles['video-detail-loading-indicator']} />;
  if (isConverting && !isDeleted) return <p>動画変換中です。しばらくお待ちください…</p>;

  return (
    <div className={styles['video-detail-outer-container']} data-testid="video-detail__container">
      <div className={styles['video-detail-main-container']}>
        <div className={styles['video-detail-left-side']}>
          <div className={styles['video-detail-player-container']} data-testid="video-detail__player-container">
            {isDeleted ? (
              <div className={styles['video-detail-deleted-container']}>
                <div className={styles['video-detail-deleted-icon-container']}>
                  <MdError className={styles['video-detail-deleted-icon']} />
                </div>
                <h2 className={styles['video-detail-deleted-title']}>この動画は削除されました</h2>
                <p className={styles['video-detail-deleted-message']}>動画は削除されましたが、コメントなどの情報は引き続き閲覧できます。</p>
              </div>
            ) : (
              <VideoDetailPlayerSection
                videoPath={video.path ? toRelativePath(video.path) : undefined}
                videoId={video.id?.toString()}
                useStreaming={true}
                initialPosition={lastWatchSeconds}
                playerRef={playerRef}
                onReady={handlePlayerReady}
                onDuration={handleDuration}
                onProgress={handleProgress}
                onEnded={handleEnded}
                autoPlay={true}
                startMuted={isMuted}
                onAutoPlayError={handleAutoPlayError}
              />
            )}
          </div>

          <div className={styles['video-detail-info-section']} data-testid="video-detail__info-section">
            <VideoDetailInfo
              video={video}
              likesCount={likesCount}
              uploaderIconUrl={uploaderIconUrl}
              hospitalName={hospitalName}
              uploaderName={uploaderName}
              tagArray={tagArray}
              handleTagClick={handleTagClick}
              likeActive={hasLiked}
              favoriteActive={hasFavorited}
              onLike={handleLike}
              onFavorite={handleFavorite}
              onCopyLink={handleCopyLink}
              isCopied={isCopied}
              disabled={isDeleted}
              kebabMenuItems={kebabMenuItems}
              isOwner={isOwner}
            />
          </div>

          <div className={styles['video-detail-description-section']} data-testid="video-detail__description-section">
            <VideoDetailDescription
              video={video}
              tagArray={tagArray}
              handleTagClick={handleTagClick}
            />
          </div>

          {hasImages && (
            <div className={styles['video-detail-carousel-section']}>
              <h2 className={styles['video-detail-section-heading']}>術前後画像</h2>
              <VideoImageCarousel images={video.images || []} />
            </div>
          )}

          {!isDeleted && (
            <div className={styles['video-detail-materials-section']} data-testid="video-detail__materials-section">
              <VideoMaterialViewer videoId={String(video.id)} />
            </div>
          )}

          <div className={styles['video-detail-comment-section']} id="comments-section" data-testid="video-detail__comment-section">
            <CommentsSection
              videoId={video.id}
              isDeleted={isDeleted}
              onSeekToTime={handleSeekToTime}
            />
          </div>
        </div>

        <div className={styles['video-detail-right-side']}>
          <RelatedVideosList video={videoMetadataRef.current} />
        </div>
      </div>

      {likeDialogOpen && (
        <VideoDetailLikesModal
          likers={likers}
          onClose={closeLikersDialog}
        />
      )}
    </div>
  );
};

export default VideoDetail;
